import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import liftLogo from "../assets/lift-logo.png";
import {
  getPendingRequests,
  loaderIsOpen,
  subscribePending,
} from "../state/loaderActivity";

const DEFAULT_LOADER_MS = 2000;
const REDUCED_MOTION_CAP_MS = 400;

export function loaderDurationMs(
  duration = DEFAULT_LOADER_MS,
  reducedMotion = false,
): number {
  if (!reducedMotion) return duration;
  return Math.min(duration, REDUCED_MOTION_CAP_MS);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(media.matches);
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  return reduced;
}

export function LogoSpin({ size = "inline" }: { size?: "inline" | "screen" }) {
  return (
    <span
      className={size === "screen" ? "logo-spin logo-spin--screen" : "logo-spin"}
      aria-hidden="true"
    >
      <span className="logo-spin-ring" />
      <span className="logo-spin-face">
        <img src={liftLogo} alt="" />
      </span>
    </span>
  );
}

export function LogoLoader({
  duration = DEFAULT_LOADER_MS,
  open,
}: {
  duration?: number;
  open?: boolean;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const [hidden, setHidden] = useState(open === undefined ? true : !open);

  useEffect(() => {
    if (open !== undefined) {
      setHidden(!open);
      return;
    }
    setHidden(false);
    const wait = loaderDurationMs(duration, reducedMotion);
    const id = window.setTimeout(() => setHidden(true), wait);
    return () => window.clearTimeout(id);
  }, [duration, open, reducedMotion]);

  return (
    <div
      className="logo-loader"
      role="status"
      aria-label="Loading"
      hidden={hidden}
    >
      <LogoSpin size="screen" />
    </div>
  );
}

function usePendingRequests(): number {
  return useSyncExternalStore(subscribePending, getPendingRequests, () => 0);
}

export function AppLoader() {
  const reducedMotion = usePrefersReducedMotion();
  const pending = usePendingRequests();
  const [splash, setSplash] = useState(true);

  useLayoutEffect(() => {
    document.getElementById("boot-loader")?.remove();
  }, []);

  useEffect(() => {
    const wait = loaderDurationMs(DEFAULT_LOADER_MS, reducedMotion);
    const id = window.setTimeout(() => setSplash(false), wait);
    return () => window.clearTimeout(id);
  }, [reducedMotion]);

  return <LogoLoader open={loaderIsOpen(splash, pending)} />;
}

export function ThinkingStatus({ message }: { message: string }) {
  return (
    <div className="logo-thinking">
      <LogoSpin />
      <div role="status" aria-live="polite">
        <p className="logo-thinking__kicker">Thinking</p>
        <p className="logo-thinking__line">{message}</p>
      </div>
    </div>
  );
}
